import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { api, ApiError } from "@/lib/api";
import type { Assignment, LessonType, Quiz } from "@/types";

const typeLabels: Record<LessonType, string> = {
  text: "Text",
  video: "Video",
  document: "Document",
  quiz: "Quiz",
  assignment: "Assignment",
};

export function CreateLessonModal({
  courseId,
  moduleId,
  onClose,
}: {
  courseId: string;
  moduleId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [type, setType] = useState<LessonType>("text");
  const [content, setContent] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(0);
  const [quizId, setQuizId] = useState("");
  const [assignmentId, setAssignmentId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: quizzes } = useQuery({
    queryKey: ["courses", courseId, "quizzes"],
    queryFn: () => api.get<Quiz[]>(`/api/v1.0/courses/${courseId}/quizzes`),
    enabled: type === "quiz",
  });
  const { data: assignments } = useQuery({
    queryKey: ["courses", courseId, "assignments"],
    queryFn: () => api.get<Assignment[]>(`/api/v1.0/courses/${courseId}/assignments`),
    enabled: type === "assignment",
  });

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      api.post(`/api/v1.0/modules/${moduleId}/lessons`, {
        title,
        type,
        content: type === "text" ? content : undefined,
        video_url: type === "video" ? videoUrl : undefined,
        duration_minutes: durationMinutes,
        quiz_id: type === "quiz" ? quizId || null : null,
        assignment_id: type === "assignment" ? assignmentId || null : null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["courses", courseId, "modules"] });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Failed to create lesson"),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutate();
  };

  return (
    <Modal title="Add lesson" onClose={onClose}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Input label="Title" required value={title} onChange={(e) => setTitle(e.target.value)} />

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-slate-700">Type</label>
          <select
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={type}
            onChange={(e) => setType(e.target.value as LessonType)}
          >
            {Object.entries(typeLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {(type === "text" || type === "document") && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-slate-700">Content</label>
            <textarea
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              rows={5}
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>
        )}

        {type === "video" && (
          <Input label="Video URL" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://..." />
        )}

        {type === "quiz" && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-slate-700">Linked quiz</label>
            <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={quizId} onChange={(e) => setQuizId(e.target.value)}>
              <option value="">Select a quiz...</option>
              {quizzes?.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.title}
                </option>
              ))}
            </select>
            {!quizzes?.length && <p className="text-xs text-slate-400">Create a quiz first from the Quizzes section.</p>}
          </div>
        )}

        {type === "assignment" && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-slate-700">Linked assignment</label>
            <select
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={assignmentId}
              onChange={(e) => setAssignmentId(e.target.value)}
            >
              <option value="">Select an assignment...</option>
              {assignments?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.title}
                </option>
              ))}
            </select>
            {!assignments?.length && <p className="text-xs text-slate-400">Create an assignment first from the Assignments section.</p>}
          </div>
        )}

        <Input
          label="Duration (minutes)"
          type="number"
          min={0}
          value={durationMinutes}
          onChange={(e) => setDurationMinutes(Number(e.target.value))}
        />

        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending}>
            Add lesson
          </Button>
        </div>
      </form>
    </Modal>
  );
}
